/**
 * FRONT-15: Onglet Bancaire V2 - Refonte complete
 * - Mode lecture par defaut, edition au clic "Modifier"
 * - Contacts comptabilite et facturation obligatoires (pas d'accordeon)
 * - Contact facturation = 1 seul champ email
 * - Validation telephone 10 chiffres
 * - IBAN exactement 27 caracteres (FR)
 * - Checkbox acceptation conditions Centralis
 */
import React, { useState, useEffect } from 'react';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import { useProfile } from '../../../contexts/ProfileContext';
import type { BancaireData, CoordonneesData } from '../../../services/profileService';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { useAutoSaveToasts } from '../../../hooks/useAutoSaveToasts';
import { useChampsManquants } from '../hooks/useChampsManquants';
import Button from '../../../composants/Button/Button';
import './TabBancaire.css';

// Texte legal pour la checkbox d'acceptation des conditions
const CONDITIONS_TEXT = `Je reconnais être informé et accepter que Centralis se rémunère comme intermédiaire selon la modalité suivante : Facturation à l’entreprise prestataire, par la Centrale d’achat, de 5% du montant total HT des commandes reçues et acceptées par l’entreprise, par périodes trimestrielles (ou semestrielles si le volume de commandes reçues par l’entreprise est mieux adapté). La rémunération de Centralis est due dans tous les cas, à partir du moment où une commande a été diffusée et acceptée par le titulaire. Que le travail ait pu être mené ou qu’il ait été retardé ou même annulé par un acheteur, les modalités de clauses d’avances applicables ainsi que l’indemnité pour renonciation à faire exécuter une commande après validation constituent une sécurité suffisante pour honorer la rémunération de Centralis dans le délai contractuel. Le délai de paiement des factures de Centralis est de 30 jours calendaires. Passé ce délai, le prestataire sera redevable d’une indemnité forfaitaire de frais de recouvrement de 40 euros (article L 441-3 du Code de Commerce) ainsi que d’une pénalité de retard dont le taux est égal à 12%.`;

// Fonction pour formater l'IBAN (ajouter des espaces tous les 4 caracteres)
const formatIBAN = (iban: string): string => {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// Validation IBAN : format FR levé (ouverture multi-pays). Plus de préfixe FR ni de longueur figée à
// 27 ; on garde seulement l'obligation de saisie et une limite haute (34 = max ISO 13616). La
// validation par pays sera ajoutée plus tard.
const validateIBAN = (iban: string): { valid: boolean; message?: string } => {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();

  if (!cleaned) {
    return { valid: false, message: 'L\'IBAN est requis' };
  }

  if (cleaned.length > 34) {
    return { valid: false, message: 'L\'IBAN ne peut pas dépasser 34 caractères' };
  }

  return { valid: true };
};

// Fonction pour valider le BIC
const validateBIC = (bic: string): { valid: boolean; message?: string } => {
  const cleaned = bic.replace(/\s/g, '').toUpperCase();

  if (!cleaned) {
    return { valid: false, message: 'Le BIC est requis' };
  }

  if (cleaned.length !== 8 && cleaned.length !== 11) {
    return { valid: false, message: 'Le BIC doit contenir 8 ou 11 caracteres' };
  }

  if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleaned)) {
    return { valid: false, message: 'Format de BIC invalide' };
  }

  return { valid: true };
};

// Validation du numéro de TVA intracommunautaire (OBLIGATOIRE). Format FR levé (ouverture multi-pays) :
// plus de regex FR, on garde l'obligation de saisie et une limite haute (20). Validation par pays plus tard.
const validateTVA = (tva: string): { valid: boolean; message?: string } => {
  if (!tva || tva.trim() === '') {
    return {
      valid: false,
      message: 'Le numéro de TVA intracommunautaire est obligatoire'
    };
  }

  const cleaned = tva.replace(/\s/g, '').toUpperCase();

  if (cleaned.length > 20) {
    return {
      valid: false,
      message: 'Le numéro de TVA ne peut pas dépasser 20 caractères'
    };
  }

  return { valid: true };
};

// FRONT-15: Validation telephone - exactement 10 chiffres
// Meme regle pour le tel principal et celui de la comptabilite (regle mobile 06/07 levee le 2026-09-17).
const validatePhone = (phone: string): { valid: boolean; message?: string } => {
  const cleaned = phone.replace(/\D/g, '');

  if (!cleaned) {
    return { valid: false, message: 'Le téléphone est requis' };
  }

  if (cleaned.length !== 10) {
    return { valid: false, message: 'Le téléphone doit contenir exactement 10 chiffres' };
  }

  return { valid: true };
};

// Formater le telephone pour l'affichage (XX XX XX XX XX)
const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length !== 10) return phone;
  return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
};

const TabBancaire: React.FC = () => {
  const { profile, updateBancaire, updateCoordonnees, reportAutoSaveStatus } = useProfile();
  const { classeChamp, estManquant } = useChampsManquants();

  const [initialized, setInitialized] = useState(false);

  // Donnees bancaires avec TVA
  const [bancaireData, setBancaireData] = useState<BancaireData & { numero_tva?: string }>({
    nom_titulaire: '',
    banque: '',
    IBAN: '',
    BIC: '',
    numero_tva: '',
  });

  // FRONT-15: Donnees coordonnees avec acceptation conditions
  const [coordonneesData, setCoordonneesData] = useState<CoordonneesData>({
    legal_nom: '',
    legal_prenom: '',
    legal_fonction: '',
    legal_email: '',
    legal_tel: '',
    compta_nom: '',
    compta_prenom: '',
    compta_fonction: '',
    compta_email: '',
    compta_tel: '',
    facture_email: '',
    acceptation_conditions_centralis: false,
  });

  // Erreurs de validation
  const [errors, setErrors] = useState<{
    IBAN?: string;
    BIC?: string;
    numero_tva?: string;
    legal_tel?: string;
    compta_tel?: string;
    acceptation?: string;
  }>({});

  // Charger les donnees du profil
  // BUGFIX: Utiliser l'IBAN complet du profil pour le mode edition
  useEffect(() => {
    // Ne reset que si le profil a vraiment changé (premier chargement ou changement utilisateur)
    if (profile?.bancaire) {
      setBancaireData(prev => {
        const p = profile.bancaire!;
        // Si aucune différence, ne pas reset
        if (
          prev.nom_titulaire === (p.nom_titulaire || '') &&
          prev.banque === (p.banque || '') &&
          prev.IBAN === (p.IBAN || '') &&
          prev.BIC === (p.BIC || '') &&
          prev.numero_tva === (p.numero_tva || '')
        ) {
          return prev;
        }
        return {
          nom_titulaire: p.nom_titulaire || '',
          banque: p.banque || '',
          IBAN: p.IBAN || '',
          BIC: p.BIC || '',
          numero_tva: p.numero_tva || '',
        };
      });
    }

    if (profile?.coordonnees) {
      setCoordonneesData(prev => {
        const c = profile.coordonnees!;
        if (
          prev.legal_nom === (c.legal_nom || '') &&
          prev.legal_prenom === (c.legal_prenom || '') &&
          prev.legal_fonction === (c.legal_fonction || '') &&
          prev.legal_email === (c.legal_email || '') &&
          prev.legal_tel === (c.legal_tel || '') &&
          prev.compta_nom === (c.compta_nom || '') &&
          prev.compta_prenom === (c.compta_prenom || '') &&
          prev.compta_fonction === (c.compta_fonction || '') &&
          prev.compta_email === (c.compta_email || '') &&
          prev.compta_tel === (c.compta_tel || '') &&
          prev.facture_email === (c.facture_email || '') &&
          prev.acceptation_conditions_centralis === (c.acceptation_conditions_centralis || false)
        ) {
          return prev;
        }
        return {
          legal_nom: c.legal_nom || '',
          legal_prenom: c.legal_prenom || '',
          legal_fonction: c.legal_fonction || '',
          legal_email: c.legal_email || '',
          legal_tel: c.legal_tel || '',
          compta_nom: c.compta_nom || '',
          compta_prenom: c.compta_prenom || '',
          compta_fonction: c.compta_fonction || '',
          compta_email: c.compta_email || '',
          compta_tel: c.compta_tel || '',
          facture_email: c.facture_email || '',
          acceptation_conditions_centralis: c.acceptation_conditions_centralis || false,
        };
      });
    }
    setInitialized(true);
  }, [profile]);

  // Autosave : enabled seulement après initialisation
  // La validation des erreurs de format se fait dans le saveFn (pas dans enabled)
  // car gater enabled sur les erreurs provoque un reset silencieux du diff dans useAutoSave
  const bancaireEnabled = initialized;
  const coordEnabled = initialized;

  const autoSaveBancaire = useAutoSave({
    data: bancaireData,
    saveFn: async (d) => {
      if (errors.IBAN || errors.BIC || errors.numero_tva) return;
      await updateBancaire(d);
    },
    delay: 1500,
    enabled: bancaireEnabled,
  });

  const autoSaveCoord = useAutoSave({
    data: coordonneesData,
    saveFn: async (d) => {
      if (errors.legal_tel || errors.compta_tel) return;
      await updateCoordonnees(d);
    },
    delay: 1500,
    enabled: coordEnabled,
  });

  // Etats d'autosave + erreurs de validation (tous champs) refletes en toasts empilables.
  useAutoSaveToasts(
    [
      { status: autoSaveBancaire.status, error: autoSaveBancaire.error },
      { status: autoSaveCoord.status, error: autoSaveCoord.error },
    ],
    { blockingError: Object.values(errors).find(Boolean) ?? null },
  );

  // Signaler les modifications en attente au contexte
  useEffect(() => {
    const isPending = autoSaveBancaire.status === 'pending' || autoSaveBancaire.status === 'saving'
      || autoSaveCoord.status === 'pending' || autoSaveCoord.status === 'saving';
    reportAutoSaveStatus('bancaire', isPending);
    return () => reportAutoSaveStatus('bancaire', false);
  }, [autoSaveBancaire.status, autoSaveCoord.status, reportAutoSaveStatus]);

  // Gestion du changement d'IBAN
  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, IBAN: value }));

    if (value.length > 0) {
      const validation = validateIBAN(value);
      setErrors(prev => ({ ...prev, IBAN: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, IBAN: undefined }));
    }
  };

  // Gestion du changement de BIC
  const handleBICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, BIC: value }));

    if (value.length > 0) {
      const validation = validateBIC(value);
      setErrors(prev => ({ ...prev, BIC: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, BIC: undefined }));
    }
  };

  // Gestion du changement de TVA
  const handleTVAChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, numero_tva: value }));

    if (value.length > 0) {
      const validation = validateTVA(value);
      setErrors(prev => ({ ...prev, numero_tva: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, numero_tva: undefined }));
    }
  };

  // FRONT-20: Validation au blur pour TVA
  const handleTVABlur = () => {
    const validation = validateTVA(bancaireData.numero_tva || '');
    setErrors(prev => ({
      ...prev,
      numero_tva: validation.valid ? undefined : validation.message
    }));
  };

  // Gestion des autres champs bancaires
  const handleBancaireChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBancaireData(prev => ({ ...prev, [name]: value }));
  };

  // FRONT-15: Gestion des telephones avec validation 10 chiffres
  const handlePhoneChange = (field: 'legal_tel' | 'compta_tel') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCoordonneesData(prev => ({ ...prev, [field]: value }));

    if (value.length > 0) {
      const validation = validatePhone(value);
      setErrors(prev => ({ ...prev, [field]: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Gestion des champs coordonnees
  const handleCoordonneesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCoordonneesData(prev => ({ ...prev, [name]: value }));
  };

  // FRONT-15: Gestion de la checkbox d'acceptation
  const handleAcceptationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCoordonneesData(prev => ({ ...prev, acceptation_conditions_centralis: checked }));
    if (checked) {
      setErrors(prev => ({ ...prev, acceptation: undefined }));
    }
  };

  return (
    <div className="tab-bancaire">
      <form className="bancaire-form">

        {/* BLOC 1: Coordonnees facturation */}
        <SectionPliable title="Coordonnées facturation">

            {/* Representant legal */}
            <div className="form-subsection">
              <h4>Representant légal<span className="required-asterisk">*</span></h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="legal_prenom">Prénom<span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="legal_prenom"
                    name="legal_prenom"
                    value={coordonneesData.legal_prenom}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('legal_prenom')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_nom">Nom<span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="legal_nom"
                    name="legal_nom"
                    value={coordonneesData.legal_nom}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('legal_nom')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_fonction">Fonction</label>
                  <input
                    type="text"
                    id="legal_fonction"
                    name="legal_fonction"
                    value={coordonneesData.legal_fonction || ''}
                    onChange={handleCoordonneesChange}
                    placeholder="Ex: Gerant, President..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_email">Email<span className="required-asterisk">*</span></label>
                  <input
                    type="email"
                    id="legal_email"
                    name="legal_email"
                    value={coordonneesData.legal_email}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('legal_email')}
                    required
                  />
                </div>
                <div className={`form-group ${errors.legal_tel ? 'has-error' : ''}`}>
                  <label htmlFor="legal_tel">Téléphone<span className="required-asterisk">*</span></label>
                  <input
                    type="tel"
                    id="legal_tel"
                    value={coordonneesData.legal_tel}
                    onChange={handlePhoneChange('legal_tel')}
                    className={classeChamp('legal_tel', !!errors.legal_tel)}
                    placeholder="0612345678"
                    maxLength={10}
                    required
                  />
                  {errors.legal_tel && <span className="error-message">{errors.legal_tel}</span>}
                  <span className="field-hint">10 chiffres</span>
                </div>
              </div>
            </div>

            {/* Contact comptabilite - OBLIGATOIRE (pas d'accordeon) */}
            <div className="form-subsection">
              <div className="subsection-header">
                <h4>Contact comptabilité<span className="required-asterisk">*</span></h4>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="compta_prenom">Prénom<span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="compta_prenom"
                    name="compta_prenom"
                    value={coordonneesData.compta_prenom || ''}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('compta_prenom')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_nom">Nom<span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="compta_nom"
                    name="compta_nom"
                    value={coordonneesData.compta_nom || ''}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('compta_nom')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_fonction">Fonction</label>
                  <input
                    type="text"
                    id="compta_fonction"
                    name="compta_fonction"
                    value={coordonneesData.compta_fonction || ''}
                    onChange={handleCoordonneesChange}
                    placeholder="Ex: Comptable, DAF..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_email">Email<span className="required-asterisk">*</span></label>
                  <input
                    type="email"
                    id="compta_email"
                    name="compta_email"
                    value={coordonneesData.compta_email || ''}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('compta_email')}
                    required
                  />
                </div>
                <div className={`form-group ${errors.compta_tel ? 'has-error' : ''}`}>
                  <label htmlFor="compta_tel">Téléphone<span className="required-asterisk">*</span></label>
                  <input
                    type="tel"
                    id="compta_tel"
                    value={coordonneesData.compta_tel || ''}
                    onChange={handlePhoneChange('compta_tel')}
                    className={classeChamp('compta_tel', !!errors.compta_tel)}
                    placeholder="0123456789"
                    maxLength={10}
                    required
                  />
                  {errors.compta_tel && <span className="error-message">{errors.compta_tel}</span>}
                  <span className="field-hint">10 chiffres requis</span>
                </div>
              </div>
            </div>

            {/* Contact facturation - email + checkbox conditions */}
            <div className="form-subsection">
              <div className="subsection-header">
                <h4>Adresse email de diffusion de facture de rémuneration Centralis<span className="required-asterisk">*</span></h4>
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="facture_email">Email<span className="required-asterisk">*</span></label>
                  <input
                    type="email"
                    id="facture_email"
                    name="facture_email"
                    value={coordonneesData.facture_email || ''}
                    onChange={handleCoordonneesChange}
                    className={classeChamp('facture_email')}
                    placeholder="factures@entreprise.fr"
                    required
                  />
                </div>
              </div>
              {/* Checkbox acceptation conditions inline.
                  Une bordure rouge sur une case a cocher de 20px serait illisible : le
                  manque est donc signale sur le conteneur, via le `has-error` deja
                  utilise pour l'erreur de validation. */}
              <div className={`checkbox-group-inline ${errors.acceptation || estManquant('acceptation_conditions_centralis') ? 'has-error' : ''}`}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    id="acceptation_conditions_centralis"
                    name="acceptation_conditions_centralis"
                    checked={coordonneesData.acceptation_conditions_centralis || false}
                    onChange={handleAcceptationChange}
                  />
                  <span className="checkbox-text">{CONDITIONS_TEXT}</span>
                </label>
                {errors.acceptation && <span className="error-message">{errors.acceptation}</span>}
              </div>
            </div>
        </SectionPliable>

        {/* BLOC 2: Coordonnees bancaires */}
        <SectionPliable title="Coordonnées bancaires">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="nom_titulaire">Nom du titulaire<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="nom_titulaire"
                  name="nom_titulaire"
                  value={bancaireData.nom_titulaire}
                  onChange={handleBancaireChange}
                  className={classeChamp('nom_titulaire')}
                  placeholder="Nom du titulaire du compte"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="banque">Nom de la banque<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="banque"
                  name="banque"
                  value={bancaireData.banque}
                  onChange={handleBancaireChange}
                  className={classeChamp('banque')}
                  placeholder="Ex: BNP Paribas, Credit Agricole..."
                  required
                />
              </div>

              <div className={`form-group ${errors.IBAN ? 'has-error' : ''}`}>
                <label htmlFor="IBAN">IBAN<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="IBAN"
                  value={formatIBAN(bancaireData.IBAN)}
                  onChange={handleIBANChange}
                  className={classeChamp('IBAN', !!errors.IBAN)}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  maxLength={42}
                  required
                />
                {errors.IBAN && <span className="error-message">{errors.IBAN}</span>}
              </div>

              <div className={`form-group ${errors.BIC ? 'has-error' : ''}`}>
                <label htmlFor="BIC">BIC / SWIFT<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="BIC"
                  name="BIC"
                  value={bancaireData.BIC}
                  onChange={handleBICChange}
                  className={classeChamp('BIC', !!errors.BIC)}
                  placeholder="BNPAFRPP"
                  maxLength={11}
                  required
                />
                {errors.BIC && <span className="error-message">{errors.BIC}</span>}
                <span className="field-hint">8 ou 11 caracteres</span>
              </div>

              <div className={`form-group ${errors.numero_tva ? 'has-error' : ''}`}>
                <label htmlFor="numero_tva">
                  N. TVA intracommunautaire<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="numero_tva"
                  name="numero_tva"
                  value={bancaireData.numero_tva || ''}
                  onChange={handleTVAChange}
                  onBlur={handleTVABlur}
                  className={classeChamp('numero_tva', !!errors.numero_tva)}
                  placeholder="FR12345678901"
                  maxLength={20}
                  required
                />
                {errors.numero_tva && <span className="error-message">{errors.numero_tva}</span>}
              </div>
            </div>
        </SectionPliable>

      </form>
    </div>
  );
};

export default TabBancaire;
